import { useState } from 'react'

export default function ContactForm({
  title = 'Skriv til værkstedet',
  subtitle = 'Udfyld formularen, så vender vi hurtigt tilbage til dig.',
  bookingUrl = 'https://service.dinbilpartner.dk/book-tid',
  variant = 'default', // 'default' | 'dark-minimal'
}) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    laanebil: 'Ja tak',
    message: '',
    website_url: '', // Honeypot antispam
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('')

  const validate = () => {
    const errs = {}
    if (!formData.name.trim()) errs.name = 'Venligst indtast dit navn'
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst indtast dit telefonnummer'
    } else if (!/^[\d\s+]{6,20}$/.test(formData.phone.trim())) {
      errs.phone = 'Ugyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mail'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Ugyldig e-mailadresse'
    }
    if (!formData.message.trim()) {
      errs.message = 'Venligst udfyld en kort beskrivelse'
    }
    return errs
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)
    setErrorMessage('')

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          laanebil: formData.laanebil,
          message: formData.message,
          website_url: formData.website_url, // Honeypot field
        }),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          laanebil: 'Ja tak',
          message: '',
          website_url: '',
        })
      } else {
        const errorData = await response.json().catch(() => ({}))
        setSubmitStatus('error')
        setErrorMessage(
          errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.'
        )
      }
    } catch (err) {
      setSubmitStatus('error')
      setErrorMessage('Kunne ikke oprette forbindelse. Venligst ring til værkstedet på 22 18 73 30.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // DARK-MINIMAL VARIANT (Matching design reference "Webform on right")
  if (variant === 'dark-minimal') {
    return (
      <div className="w-full">
        {submitStatus === 'success' && (
          <div className="mb-6 p-4 rounded-none bg-emerald-950/80 border border-emerald-600 text-emerald-200 text-sm">
            <div className="flex items-center gap-2 font-bold mb-1">
              <i className="fa-solid fa-circle-check text-emerald-400" aria-hidden="true" />
              <span>Mange tak for din henvendelse!</span>
            </div>
            <p>Vi har modtaget din besked og vender hurtigt tilbage til dig.</p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-6 p-4 rounded-none bg-red-950/80 border border-red-600 text-red-200 text-sm">
            <div className="flex items-center gap-2 font-bold mb-1">
              <i className="fa-solid fa-circle-exclamation text-red-400" aria-hidden="true" />
              <span>Der opstod et problem</span>
            </div>
            <p>{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Antispam honeypot field - invisible to humans */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url_dark">Website URL</label>
            <input
              id="website_url_dark"
              name="website_url"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {/* Top row splits into two fields (Name, Phone) using a 50/50 grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="dark_form_name" className="block text-xs font-semibold text-white/80 uppercase tracking-wider mb-2">
                Navn <span className="text-[#e20019]">*</span>
              </label>
              <input
                type="text"
                id="dark_form_name"
                name="name"
                required
                placeholder="Dit fulde navn"
                value={formData.name}
                onChange={handleChange}
                className={`w-full bg-transparent border-0 border-b pb-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-0 transition-colors ${
                  errors.name ? 'border-red-500' : 'border-white/40 focus:border-[#e20019]'
                }`}
              />
              {errors.name && <p className="text-red-400 text-xs mt-1.5">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="dark_form_phone" className="block text-xs font-semibold text-white/80 uppercase tracking-wider mb-2">
                Telefonnummer <span className="text-[#e20019]">*</span>
              </label>
              <input
                type="tel"
                id="dark_form_phone"
                name="phone"
                required
                placeholder="F.eks. 22 18 73 30"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full bg-transparent border-0 border-b pb-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-0 transition-colors ${
                  errors.phone ? 'border-red-500' : 'border-white/40 focus:border-[#e20019]'
                }`}
              />
              {errors.phone && <p className="text-red-400 text-xs mt-1.5">{errors.phone}</p>}
            </div>
          </div>

          {/* Subsequent fields span full width */}
          <div>
            <label htmlFor="dark_form_email" className="block text-xs font-semibold text-white/80 uppercase tracking-wider mb-2">
              E-mail <span className="text-[#e20019]">*</span>
            </label>
            <input
              type="email"
              id="dark_form_email"
              name="email"
              required
              placeholder="din@email.dk"
              value={formData.email}
              onChange={handleChange}
              className={`w-full bg-transparent border-0 border-b pb-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-0 transition-colors ${
                errors.email ? 'border-red-500' : 'border-white/40 focus:border-[#e20019]'
              }`}
            />
            {errors.email && <p className="text-red-400 text-xs mt-1.5">{errors.email}</p>}
          </div>

          {/* Loaner Car option */}
          <div>
            <label className="block text-xs font-semibold text-white/80 uppercase tracking-wider mb-2">
              Ønsker du en lånebil?
            </label>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                <input
                  type="radio"
                  name="laanebil"
                  value="Ja tak"
                  checked={formData.laanebil === 'Ja tak'}
                  onChange={handleChange}
                  className="text-[#e20019] focus:ring-red-500 bg-transparent border-white/40"
                />
                <span>Ja tak (gratis lånebil)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                <input
                  type="radio"
                  name="laanebil"
                  value="Nej tak"
                  checked={formData.laanebil === 'Nej tak'}
                  onChange={handleChange}
                  className="text-[#e20019] focus:ring-red-500 bg-transparent border-white/40"
                />
                <span>Nej tak</span>
              </label>
            </div>
          </div>

          {/* Message textarea */}
          <div>
            <label htmlFor="dark_form_message" className="block text-xs font-semibold text-white/80 uppercase tracking-wider mb-2">
              Besked / Beskriv hvad du ønsker hjælp til <span className="text-[#e20019]">*</span>
            </label>
            <textarea
              id="dark_form_message"
              name="message"
              rows={3}
              required
              placeholder="Beskriv din bil (mærke, model, årgang eller nummerplade) og hvad der skal laves..."
              value={formData.message}
              onChange={handleChange}
              className={`w-full bg-transparent border-0 border-b pb-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-0 transition-colors resize-y ${
                errors.message ? 'border-red-500' : 'border-white/40 focus:border-[#e20019]'
              }`}
            />
            {errors.message && <p className="text-red-400 text-xs mt-1.5">{errors.message}</p>}
          </div>

          {/* Solid, pill-shaped primary CTA button spanning full width */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 rounded-full bg-[#e20019] hover:bg-[#b80014] text-white font-bold uppercase tracking-wider text-xs sm:text-sm shadow-lg hover:shadow-red-900/30 transition-all flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  <span>Sender besked...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane text-white" aria-hidden="true" />
                  <span>Send henvendelse</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    )
  }

  // DEFAULT VARIANT (kept intact for existing pages)
  return (
    <div className="bg-white border border-gray-200 p-6 sm:p-8 shadow-sm">
      <div className="mb-6">
        <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-wide text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
        <div className="mt-3 text-xs text-gray-500 bg-gray-50 p-2.5 border border-gray-200 flex items-center justify-between">
          <span>Har du brug for hurtig online tidsbestilling?</span>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#e20019] font-bold hover:underline ml-2 whitespace-nowrap"
          >
            Brug Book tid &rarr;
          </a>
        </div>
      </div>

      {submitStatus === 'success' && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
          <div className="flex items-center gap-2 font-bold mb-1">
            <i className="fa-solid fa-circle-check text-emerald-600" aria-hidden="true" />
            <span>Mange tak for din henvendelse!</span>
          </div>
          <p>Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">
          <div className="flex items-center gap-2 font-bold mb-1">
            <i className="fa-solid fa-circle-exclamation text-red-600" aria-hidden="true" />
            <span>Der opstod et problem</span>
          </div>
          <p>{errorMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Antispam honeypot field - invisible to humans */}
        <div className="sr-only absolute" aria-hidden="true">
          <label htmlFor="website_url">Website URL</label>
          <input
            id="website_url"
            name="website_url"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={formData.website_url}
            onChange={handleChange}
          />
        </div>

        {/* Name */}
        <div>
          <label htmlFor="form_name" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
            Navn <span className="text-[#e20019]">*</span>
          </label>
          <input
            type="text"
            id="form_name"
            name="name"
            required
            placeholder="Dit fulde navn"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
              errors.name ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
            }`}
          />
          {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Phone & Email Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="form_phone" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Telefonnummer <span className="text-[#e20019]">*</span>
            </label>
            <input
              type="tel"
              id="form_phone"
              name="phone"
              required
              placeholder="F.eks. 22 18 73 30"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
                errors.phone ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
              }`}
            />
            {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="form_email" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              E-mail <span className="text-[#e20019]">*</span>
            </label>
            <input
              type="email"
              id="form_email"
              name="email"
              required
              placeholder="din@email.dk"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
                errors.email ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
              }`}
            />
            {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
          </div>
        </div>

        {/* Loaner car question as seen in scrape */}
        <div>
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
            Ønsker du en lånebil?
          </label>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
              <input
                type="radio"
                name="laanebil"
                value="Ja tak"
                checked={formData.laanebil === 'Ja tak'}
                onChange={handleChange}
                className="text-[#e20019] focus:ring-red-500"
              />
              <span>Ja tak</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
              <input
                type="radio"
                name="laanebil"
                value="Nej tak"
                checked={formData.laanebil === 'Nej tak'}
                onChange={handleChange}
                className="text-[#e20019] focus:ring-red-500"
              />
              <span>Nej tak</span>
            </label>
          </div>
        </div>

        {/* Message / Description */}
        <div>
          <label htmlFor="form_message" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
            Beskriv kort hvad du ønsker hjælp til <span className="text-[#e20019]">*</span>
          </label>
          <textarea
            id="form_message"
            name="message"
            rows={4}
            required
            placeholder="Beskriv din bil (mærke, model, årgang eller nummerplade) og hvad der skal laves..."
            value={formData.message}
            onChange={handleChange}
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
              errors.message ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
            }`}
          />
          {errors.message && <p className="text-red-600 text-xs mt-1">{errors.message}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#14171a] hover:bg-[#20252b] text-white px-7 py-3.5 rounded-none font-bold text-xs uppercase tracking-[0.2em] transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <span>Sender besked...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane text-red-500" aria-hidden="true" />
              <span>Send henvendelse</span>
            </>
          )}
        </button>
      </form>
    </div>
  )
}
